import { MessagingService } from "@app/core/messaging/messaging.service";
import { applicationClientId, endpointBase } from "@app/config/api.config";
import { AuthResponse, Response } from "../models/response";
import { BehaviorSubject, catchError, delay, from, mergeMap, Observable, of, switchMap, tap } from "rxjs";
import { Customer } from "../models/customer";
import { Geolocation } from "@capacitor/geolocation";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LinkService } from "./link.service";
import { LogoutAction, LogoutContext, TokenManagerService } from "./token-manager.service";
import { ModalController, NavController, Platform } from "@ionic/angular";
import { normalizeCustomerBalances } from "../helpers/points.helper";
import { RegisterService } from "./register.service";
import { StorageService } from "./storage.service";
import { User } from "../models/user";
import { withTimeout } from "../core/compatibility/with-timeout";
import { logMinimumOrderResponse } from "../util/minimum-order-diagnostics";

const MAX_RETRY = 3;

@Injectable({
  providedIn: "root",
})
export class SharedService {
  user?: User;
  customer?: Customer;
  user$: BehaviorSubject<User | undefined> = new BehaviorSubject<User | undefined>(undefined);
  customer$: BehaviorSubject<Customer | undefined> = new BehaviorSubject<Customer | undefined>(undefined);
  htmlTerms;
  htmlPrivacy;
  notifications;

  web = false;
  ios = false;

  constructor(
    private http: HttpClient,
    public platform: Platform,
    public storage: StorageService,
    public link: LinkService,
    public navCtrl: NavController,
    public modalController: ModalController,
    public register: RegisterService,
    public tokenManager: TokenManagerService,
    private messaging: MessagingService
  ) {
    this.user$.next(undefined);
    this.listenUser();
    this.getTermsAndPrivacy();
  }

  // ============================================
  // TOKEN ACCESSORS (delegados ao TokenManager)
  // ============================================

  get accessToken(): string | undefined {
    return this.tokenManager.accessToken || undefined;
  }

  set accessToken(token: string | undefined) {
    this.tokenManager.accessToken = token || null;
  }

  get refreshToken(): string | undefined {
    return this.tokenManager.refreshToken || undefined;
  }

  set refreshToken(token: string | undefined) {
    this.tokenManager.refreshToken = token || null;
  }

  getNotifications() {
    return this.get("register/notifications").pipe(
      tap((res) => {
        if (res?.status) {
          this.notifications = res.data;
        } else {
          this.notifications = [];
        }
      })
    );
  }

  readTermsClubOrbitta() {
    return this.post<any>("engine/customer/v2/customer/accept_terms", {}).pipe(tap((res) => {}));
  }

  loadCustomer() {
    return this.get<Customer>("engine/customer/v2/customer").pipe(
      tap((res) => {
        if (res.status) {
          this.setCustomer(res.data);
        }
      })
    );
  }

  /**
   * Ponto único de atualização do customer. Normaliza os saldos (pontos nunca
   * negativos) antes de propagar para APP e WEB.
   */
  setCustomer(customer?: Customer) {
    this.customer = normalizeCustomerBalances(customer);
    this.customer$.next(this.customer);
  }

  get<T>(endpoint: string, avoidParcialMaintenance?: boolean): Observable<Response<T>> {
    return this.makeRequest<T>("GET", endpoint, avoidParcialMaintenance);
  }

  post<T>(endpoint: string, data: any, isAuth = false, geolocation = false, avoidParcialMaintenance?: boolean): Observable<Response<T>> {
    const contentType = isAuth ? "urlencoded" : "json";
    return from(this.getUpdatedData(data, geolocation)).pipe(
      switchMap((updatedData) => {
        return this.makeRequest<T>("POST", endpoint, updatedData, contentType, avoidParcialMaintenance);
      })
    );
  }

  put<T>(endpoint: string, data: any, geolocation = false, avoidParcialMaintenance?: boolean): Observable<Response<T>> {
    return from(this.getUpdatedData(data, geolocation)).pipe(
      switchMap((updatedData) => {
        return this.makeRequest<T>("PUT", endpoint, updatedData, "json", avoidParcialMaintenance);
      })
    );
  }

  delete<T>(endpoint: string, data?: any, geolocation = false, avoidParcialMaintenance?: boolean): Observable<Response<T>> {
    return from(this.getUpdatedData(data, geolocation)).pipe(
      switchMap((updatedData) => {
        return this.makeRequest<T>("DELETE", endpoint, updatedData, "json", avoidParcialMaintenance);
      })
    );
  }

  signin(username: string, password: string): Observable<Response<AuthResponse>> {
    const data = {
      username: username,
      password: password,
      grant_type: "password",
      client_id: applicationClientId,
    };
    return this.post<AuthResponse>("auth", data, true);
  }

  resetUser() {
    this.tokenManager.reset();
    this.user = undefined;
    this.register.clearClient(true);
    void Promise.all(["auth", "currentCategory", "currentBrand", "currentProduct", "sessionId", "biometric"]
      .map(key => this.storage.set(key, null)))
      .catch(error => console.warn("[Auth] Não foi possível limpar o cache local", error));
    this.user$.next(undefined);
  }

  logout(action: LogoutAction | string, context?: LogoutContext): Observable<Response<AuthResponse>> {
    // Log detalhado antes do logout
    const logData = {
      action,
      context,
      timestamp: new Date().toISOString(),
      clientId: this.user?.clientId,
      email: this.user?.responsible?.email,
      tokenState: this.tokenManager.tokenState,
    };
    console.error(`[AUTH] Logout triggered:`, logData);

    const data = {
      client_id: applicationClientId,
      refresh_token: this.refreshToken ? this.refreshToken : "",
      action,
      // Envia contexto adicional para o backend poder debugar
      context: context ? JSON.stringify(context) : undefined,
    };
    if (data.refresh_token === "") {
      delete data.refresh_token;
    }
    if (!data.context) {
      delete data.context;
    }

    return from(this.messaging.clearUser()).pipe(
      switchMap(() => this.post<any>(`auth/logout?clientId=${this.user?.clientId}&action=${action}&email=${this.user?.responsible?.email}`, data, true)),
      tap(() => {
        this.resetUser();
        this.navCtrl.navigateRoot("/login");
      }),
      catchError((error) => {
        console.error(`[AUTH] Logout API error (will reset anyway):`, error);
        this.resetUser();
        this.navCtrl.navigateRoot("/login");
        return of({
          httpStatus: null as any,
          status: false,
          message: null as any,
          data: null as any,
          maintenanceParcial: null as any,
          maintenanceTotal: null as any,
        });
      })
    );
  }

  syncPushRegistration() {
    void this.messaging.sync();
    return of({ status: true });
  }

  /**
   * Refresh de token delegado ao TokenManager centralizado.
   * Isso garante que apenas UM refresh aconteça por vez,
   * mesmo com múltiplas requisições HTTP e gRPC simultâneas.
   */
  public refresh(source: "HTTP" | "GRPC" = "HTTP"): Observable<{ status: boolean; action?: LogoutAction; context?: LogoutContext }> {
    return this.tokenManager.refreshTokens(source).pipe(
      tap((result) => {
        if (result.status) {
          void this.storage.get("auth").then((auth) => {
            if (auth?.user) {
              this.user = auth.user;
              this.user$.next(auth.user);
            }
          });
        }
      }),
    );
  }

  private makeRequest<T>(method: string, endpoint: string, data?: any, contentType: "json" | "urlencoded" = "json", avoidParcialMaintenance?: boolean): Observable<Response<T>> {
    const clientId = this.user?.clientId;
    const url = `${endpointBase}/${endpoint}`;
    const headers = new HttpHeaders({
      "Content-Type": contentType === "json" ? "application/json" : "application/x-www-form-urlencoded",
      Authorization: `Bearer ${this.accessToken && contentType === "json" ? this.accessToken : ""}`,
    });
    const body = contentType === "json" ? data : new URLSearchParams(data).toString();

    return this.http.request<Response<T>>(method, url, { headers, body, observe: "response" }).pipe(
      switchMap((response: HttpResponse<Response<T>>) => {
        logMinimumOrderResponse({ clientId, method, endpoint, httpStatus: response.status }, response.body);
        return this.handleResponse(response, avoidParcialMaintenance);
      }),
      catchError((error: HttpErrorResponse) => {
        logMinimumOrderResponse({ clientId, method, endpoint, httpStatus: error.status }, error.error);
        return this.handleError<T>(error, url, () => this.makeRequest(method, endpoint, data, contentType), 0);
      })
    );
  }

  private handleResponse<T>(response: HttpResponse<Response<T>>, avoidParcialMaintenance?: boolean): Observable<Response<T>> {
    const maintenanceTotal = response.headers.get("x-maintenance-total") === "true";
    const maintenancePartial = response.headers.get("x-maintenance-partial") === "true";

    if (maintenanceTotal || (maintenancePartial && !avoidParcialMaintenance)) {
      this.navCtrl.navigateRoot("/maintenance");
    }

    return of({
      httpStatus: response.status,
      status: true,
      message: response.body?.message ?? "",
      data: response.body?.data as any,
      maintenanceParcial: maintenancePartial,
      maintenanceTotal: maintenanceTotal,
    });
  }

  private handleError<T>(error: HttpErrorResponse, requestUrl: string, originalRequest: () => Observable<Response<T>>, retryCount: number = 0): Observable<Response<T>> {
    const errorData = error.error as Response<T> | null;
    const maintenancePartial = error.headers?.get("x-maintenance-partial") === "true";
    const maintenanceTotal = error.headers?.get("x-maintenance-total") === "true";

    // Caso 1: 401 sem refreshToken
    if (error.status === 401 && !this.refreshToken) {
      const context: LogoutContext = {
        httpStatus: error.status,
        errorMessage: errorData?.message as any,
        requestUrl,
        hadRefreshToken: false,
        refreshAttempts: retryCount,
        source: "HTTP",
      };

      const action: LogoutAction = this.tokenManager.tokenState.isValid ? "NO_REFRESH_TOKEN_LOST" : "NO_REFRESH_TOKEN_INITIAL";

      console.error(`[AUTH HTTP] 401 received without refreshToken`, context);
      this.logout(action, context).subscribe();

      return of({
        httpStatus: error.status,
        status: false,
        message: errorData?.message as any,
        data: errorData as any,
        maintenanceParcial: maintenancePartial,
        maintenanceTotal: maintenanceTotal,
      } as Response<T>);
    }

    // Caso 2: 401 com refreshToken - tenta refresh
    if (error.status === 401 && this.refreshToken) {
      if (!this.tokenManager.canRetryRefresh() && retryCount >= MAX_RETRY) {
        const context: LogoutContext = {
          httpStatus: error.status,
          requestUrl,
          hadRefreshToken: true,
          refreshAttempts: retryCount,
          source: "HTTP",
        };

        console.error(`[AUTH HTTP] Max retries exhausted`, context);
        this.logout("MAX_RETRIES_EXHAUSTED", context).subscribe();

        return of({
          httpStatus: error.status,
          status: false,
          message: "Token refresh failed after maximum retries",
          data: errorData as any,
          maintenanceParcial: maintenancePartial,
          maintenanceTotal: maintenanceTotal,
        } as Response<T>);
      }

      if (retryCount < MAX_RETRY) {
        console.log(`[AUTH HTTP] Attempting refresh (attempt ${retryCount + 1}/${MAX_RETRY})`);

        return this.refresh("HTTP").pipe(
          switchMap((refreshResponse) => {
            if (refreshResponse.status) {
              console.log(`[AUTH HTTP] Refresh successful, retrying original request`);
              return originalRequest();
            } else {
              if (retryCount + 1 < MAX_RETRY && this.tokenManager.canRetryRefresh()) {
                const delayTime = Math.pow(2, retryCount + 1) * 1000;
                console.warn(`[AUTH HTTP] Refresh failed (${retryCount + 1}/${MAX_RETRY}), retrying after ${delayTime}ms`);

                return of(null).pipe(
                  delay(delayTime),
                  mergeMap(() => this.handleError(error, requestUrl, originalRequest, retryCount + 1))
                );
              } else {
                const action = refreshResponse.action || "REFRESH_FAILED_INVALID";
                const context = refreshResponse.context || this.tokenManager.getContext({ source: "HTTP", requestUrl });

                console.error(`[AUTH HTTP] Refresh failed permanently`, { action, context });
                this.logout(action, context).subscribe();

                return of({
                  httpStatus: error.status,
                  status: false,
                  message: "Token refresh failed",
                  data: errorData as any,
                  maintenanceParcial: maintenancePartial,
                  maintenanceTotal: maintenanceTotal,
                } as Response<T>);
              }
            }
          }),
          catchError((innerError) => {
            const context: LogoutContext = {
              httpStatus: innerError.status,
              errorMessage: innerError.message,
              requestUrl,
              hadRefreshToken: true,
              refreshAttempts: retryCount,
              source: "HTTP",
              originalError: innerError,
            };

            console.error(`[AUTH HTTP] Refresh exception`, context);
            this.logout("REFRESH_FAILED_NETWORK", context).subscribe();

            return of({
              httpStatus: innerError.status,
              status: false,
              message: "Token refresh failed due to network error",
              data: innerError.error,
              maintenanceParcial: false,
              maintenanceTotal: false,
            } as Response<T>);
          })
        );
      }
    }

    // Caso 3: Outros erros (não 401)
    return of({
      httpStatus: error.status,
      status: false,
      message: errorData?.message ?? "",
      data: errorData as any,
      maintenanceParcial: maintenancePartial,
      maintenanceTotal: maintenanceTotal,
    } as Response<T>);
  }

  private listenUser() {
    this.user$.subscribe(user => {
      this.user = user;
      this.messaging.setUser(user?.clientId ? String(user.clientId) : undefined);
    });
  }

  async initUserLocation() {
    try {
      const permissions = await withTimeout(Geolocation.checkPermissions(), 3000, "Permissão de localização");
      if (permissions.location === "granted") {
        const coordinates = await withTimeout(Geolocation.getCurrentPosition({
          enableHighAccuracy: true, timeout: 10000, maximumAge: 300000,
        }), 12000, "Localização");
        if (Number.isFinite(coordinates?.coords?.latitude) && Number.isFinite(coordinates?.coords?.longitude)) {
          return {
            latitude: coordinates.coords.latitude,
            longitude: coordinates.coords.longitude,
          };
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error) {
      console.warn("[Location] Recurso indisponível; continuando sem coordenadas", error);
      return null;
    }
  }

  private async getUpdatedData(data: any, geolocation?: boolean) {
    data ??= {};
    try {
      if (geolocation) {
        const coords: { latitude: number; longitude: number } = await this.initUserLocation();

        if (coords) {
          data.latitude = coords.latitude;
          data.longitude = coords.longitude;
        }
      }
    } catch (err) {
      console.error("Error initUserLocation: ", err);
    }
    return data;
  }

  private getTermsAndPrivacy() {
    this.get<{ terms: string; privacy: string }>("terms-and-privacy", true).subscribe({
      next: (res) => {
        if (res?.data) {
          this.htmlTerms = res.data.terms;
          this.htmlPrivacy = res.data.privacy;
        }
      },
      error: (err) => {
        console.error("[Startup] Erro ao carregar termos e privacidade (não fatal):", err);
      },
    });
  }
}
